import { Link } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, Heart } from "lucide-react";
import { AuthImage } from "./AuthImage";

/**
 * Sortable variant of the dashboard list card.
 * - Drag handle on desktop (hover) and mobile (always visible).
 * - Up/Down buttons always visible — primary mobile-friendly reorder UX.
 * - Card body remains a Link so navigation still works.
 */
export const SortableListCard = ({
  lst,
  IconComp = Heart,
  disabled = false,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lst.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  const progress = lst.entry_count
    ? Math.round((lst.completed_count / lst.entry_count) * 100)
    : 0;

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`sortable-list-${lst.id}`}>
      {!disabled && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1">
          <button
            type="button"
            aria-label="Liste nach oben"
            disabled={!canMoveUp}
            onClick={(e) => { stop(e); canMoveUp && onMoveUp?.(lst.id); }}
            className="p-2 rounded-full bg-white/90 border border-border text-text_secondary hover:text-ink hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`list-move-up-${lst.id}`}
          >
            <ChevronUp size={14} strokeWidth={1.8} />
          </button>
          <button
            type="button"
            aria-label="Liste nach unten"
            disabled={!canMoveDown}
            onClick={(e) => { stop(e); canMoveDown && onMoveDown?.(lst.id); }}
            className="p-2 rounded-full bg-white/90 border border-border text-text_secondary hover:text-ink hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`list-move-down-${lst.id}`}
          >
            <ChevronDown size={14} strokeWidth={1.8} />
          </button>
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label="Liste verschieben"
            className="hidden sm:inline-flex p-2 rounded-full bg-white/90 border border-border text-text_secondary hover:text-ink hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none"
            onClick={(e) => e.stopPropagation()}
            data-testid={`list-drag-handle-${lst.id}`}
          >
            <GripVertical size={14} strokeWidth={1.7} />
          </button>
        </div>
      )}
      <Link
        to={`/lists/${lst.id}`}
        className="bg-white border border-border rounded-2xl overflow-hidden hover:border-ink/30 transition-colors flex flex-col h-full"
        data-testid={`list-card-${lst.id}`}
      >
        <div className="aspect-[16/9] relative" style={{ backgroundColor: lst.color }}>
          {lst.cover_image && (
            <AuthImage fileId={lst.cover_image} size="medium" className="absolute inset-0 w-full h-full object-cover" alt={lst.title} />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-black/0 to-black/25" />
          <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
            <IconComp size={18} strokeWidth={1.6} className="text-ink" />
          </div>
          <div className="absolute bottom-4 right-4 text-white/90 text-xs uppercase tracking-[0.2em]">
            {lst.entry_count || 0} {lst.entry_count === 1 ? "Eintrag" : "Einträge"}
          </div>
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="font-serif text-2xl text-ink leading-tight">{lst.title}</h3>
          {lst.description && (
            <p className="text-sm text-text_secondary mt-2 line-clamp-2">{lst.description}</p>
          )}
          <div className="mt-auto pt-6">
            <div className="flex items-center justify-between text-xs text-text_secondary">
              <span>{lst.completed_count || 0} erlebt</span>
              <span>{progress}%</span>
            </div>
            <div className="mt-2 h-1 bg-bone rounded-full overflow-hidden">
              <div className="h-full bg-ink rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default SortableListCard;
